<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
    </div>
    <McGrapqlTable
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('offlineLastPlace')"
      reportKey="vehicle-offline"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle.licensePlate ?? '-' }}
      </template>
      <template #cell(imei)="data">
        {{ data.item.vehicle.imei ?? '-' }}
      </template>
      <template #cell(deviceType)="data">
        {{ data.item.vehicle.device?.deviceType ?? '-' }}
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>
      <template #cell(vgroups)="data">
        {{ data.item.vgroups !== null ? data.item.vgroups : '' }}
      </template>
      <template #cell(offlineDate)="data">
        {{ momentDateShortMonth(data.item.offlineDate) }}<br />
        <span class="text-muted">{{ momentTimeSeconds(data.item.offlineDate, true) }}</span>
      </template>
      <template #cell(totalOfflineTimeSeconds)="data">
        {{ convertTime(data.item.totalOfflineTimeSeconds) }}
      </template>
      <template #cell(lastLocation)="data">
        <b-skeleton width="70%" v-if="data.item.lastLocation == '' || data.item.lastLocation == null" />
        <AddressValue
          v-else
          :id="`lastLocation-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.lastLocation"
        />
      </template>
      <template #cell(geofence)="data">
        {{ data.item.geofence }}
      </template>
      <template #cell(lastAlert)="data">
        <div v-for="(text, index) in viewHistoryAlert(data.item.lastAlert)" :key="index">
          {{ text }}
        </div>
      </template>
    </McGrapqlTable>
    <McReportDownloader />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import { GET_OFFLINE_VEHICLE_REPORT } from '@/services/api/report/report'
import { EXPORT_PDF_REPORT_OFFLINE_VEHICLE } from '@/services/api/report/pdf'
import { EXPORT_EXCEL_REPORT_OFFLINE_VEHICLE } from '@/services/api/report/excel'
import { MixPanel } from '@/mixin/mixpanel'
import reportList from '../../lib/reportList.js'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { mapGetters } from 'vuex'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import AddressValue from '../../components/AddressValue.vue'

moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    FavoriteReportButton,
    AddressValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'offline-vehicle',
      queryCache: new QueryCache(),
    }
  },
  mounted() {
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    apiClient() {
      return {
        get: async (filter) => {
          const self = this
          const res = await this.queryCache.fetch(
            GET_OFFLINE_VEHICLE_REPORT,
            {
              filter: {
                ...filter,
                column: this.columns.filter((item) => item != 'offlineLastPlace'),
              },
            },
            self.reportKey
          )

          const result = res.reportVehiclesOffline.reportVehiclesOffline
          return result
        },
      }
    },
    report() {
      return reportList.find((item) => item.key == this.reportKey)
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
    },
    redirToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    formatTimePart(value) {
      return value < 10 ? `0${value}` : value
    },
    formatTime(hours, minutes, seconds) {
      return `${this.formatTimePart(hours)}:${this.formatTimePart(minutes)}:${this.formatTimePart(
        seconds
      )}`
    },
    convertTime(time) {
      if (time == null || time === '') {
        return ''
      }

      time = Math.floor(time)

      const seconds = time % 60
      const minutes = Math.floor(time / 60) % 60
      const hours = Math.floor(time / 3600) % 24
      const days = Math.floor(time / 86400)

      if (time < 60) {
        return `00:00:${this.formatTimePart(time)}`
      } else if (time < 3600) {
        return `00:${this.formatTimePart(minutes)}:${this.formatTimePart(seconds)}`
      } else if (time < 86400) {
        return this.formatTime(hours, minutes, seconds)
      } else {
        const textDay = days > 1 ? this.$t('global.time.days') : this.$t('global.time.day')
        return `${days} ${textDay} ${this.formatTime(hours, minutes, seconds)}`
      }
    },
    viewHistoryAlert(alert) {
      let texts = []
      alert.forEach((item) => {
        let lang = localStorage.getItem('language')
        let datetime = this.momentDatetimeShortMonth(item.createdOn, true)
        let text = ''
        if (lang == 'en') {
          text = `- ${item.descriptionEng} (${datetime})`
        } else {
          text = `- ${item.descriptionIdn} (${datetime})`
        }
        texts.push(text)
      })
      return texts
    },
    showDownloader(date, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name: `${this.title} ${date}.${extension}`,
        periode: date,
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id:
          extension === 'xlsx'
            ? result.excelReportVehicleOffline.id
            : result.pdfReportVehicleOffline.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const date = moment().format('DD MMMM YYYY HH:mm:ss')
      let filterVar = {
        column: this.columns,
        subtitle: `Data diambil pada : ${date}`,
      }

      const query =
        extension === 'xlsx'
          ? EXPORT_EXCEL_REPORT_OFFLINE_VEHICLE
          : EXPORT_PDF_REPORT_OFFLINE_VEHICLE

      this.$apollo
        .query({
          query: query,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let result =
            extension === 'xlsx'
              ? res.data.excelReportVehiclesOffline
              : res.data.pdfReportVehiclesOffline
          this.showDownloader(date, extension, result)
        })
    },
  },
}
</script>
